import trpc from '@tencent/tpro_rmonitor_web_server_issue_list';
import { get } from 'lodash-es';
import { getAxios } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import {
  formatKeyMethodsByJson,
  formatKeyMethodsByKeyValue,
  formatKeyMethodsByLine,
} from '@/infrastructure/format-data';
import { getParamFromUrl } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { getPid, getProductId } from '@/infrastructure/product-info';
import { decimal2Percentile } from '@/infrastructure/utils/to-unit';
import { getExceptionTypeOptions } from '@/presentation/components/exception-form/utils';
import { initPageSetting } from '@/presentation/components/issue-table/hooks/usePagination';
import { IssueFilterParams } from '@/presentation/store/issue';
import { IssueType } from '@/proto/issue';
import { issueService } from '@/service/issue';
import { WithTypeCast } from '@raft/utils';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import { toLineString } from '@raft/utils/src/case/snake-case';

export type RawTotalCount = {
  totalDocCount: number;
  totalQimeiCount: number;
};

const request = getAxios();
export const parseJson = (filter: string) => {
  try {
    return JSON.parse(filter);
  } catch (e) {
    return [];
  }
};

export const getIssueList = async (issueType: IssueType, filterParams: IssueFilterParams) => {
  const exceptionOptions = getExceptionTypeOptions(issueType);
  // FOOM 限定 errorType
  const params = issueType === IssueType.ISSUE_TYPE_FOOM_BUGLY ? {
    ...filterParams,
    errorType: '*java.lang.OutOfMemoryError*',
  } : filterParams;
  try {
    const res = await request.post(`/${API_PREFIX}/api/issue/list`, {
      appId: getProductId(),
      platformId: getPid(),
      start: 0,
      rows: 10,
      exceptionTypeList: exceptionOptions?.[0]?.value,  // 当用户删除这个选项时兜底
      searchType: 'errorType',
      ...params,
      linkages: params.linkages && typeof(params.linkages) === 'string' ? parseJson(params.linkages) : undefined,
    });
    return {
      total: get(res, 'data.numFound', 0),
      list: get(res, 'data.issueList', []),
    };
  } catch (e) {
    logger.error('getIssueList:', e);
    return {
      total: 0,
      list: [],
    };
  }
};

export type IGetIssueListReq = WithTypeCast<trpc.trpc.rmonitor.web_server.IGetIssueListReq> & {
  rows?: number;
  start?: number;
  sortOrder?: string;
};
export type IGetIssueListRsp = WithTypeCast<trpc.trpc.rmonitor.web_server.IGetIssueListRsp>;
export type IIssueInfo = WithTypeCast<trpc.trpc.rmonitor.web_server.IIssueInfo>;
export type ICommonInfo = WithTypeCast<trpc.trpc.rmonitor.web_server.ICommonInfo>;
export type IDiffInfo = WithTypeCast<trpc.trpc.rmonitor.web_server.IDiffInfo>;

export const getIssueIndex = (
  current: number,
  pageSize: number,
  index: number,
) => `${((current - 1) * pageSize) + (index + 1)}`;

export const getNewIssueList = async (params: IGetIssueListReq, issueType: IssueType, token = '') => {
  try {
    const countPage = (params.start! / params.rows!) + 1 || initPageSetting.current;
    const page = params.page ?? countPage;
    const pageSize = params.pageSize ?? params.rows ?? initPageSetting.pageSize;
    const reqBody: IGetIssueListReq = {
      page,
      pageSize,
      issueType,
      productId: getProductId(),
      sortField: getSortField(params.sortField ?? ''),
      sortType: params.sortType ?? getSortType(params.sortOrder),
      token: token || params?.token || getParamFromUrl('token'),
      compare: false,
    };


    const res: IGetIssueListRsp = await issueService.getIssueList(reqBody);
    const data = res?.issueList?.map((item: IIssueInfo, index: number) => {
      const diffResult = item?.diffResult?.map((diff: IDiffInfo) => {
        const name = toHumpString(diff?.name);
        return ({
          [`${name}Value`]: diff.value,
        });
      });

      const diffData = diffResult?.reduce((prev: any, current: any) => ({ ...prev, ...current }), {});

      const tagInfo = item?.commonResult?.tags?.map(item => ({
        tagId: item.id,
        tagName: item.name,
        tagColor: item.color,
      }));

      const getKeyMethods = () => {
        switch (issueType) {
          case IssueType.ISSUE_TYPE_FOOM:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_FOOM_MALLOC:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_BADMALLOC:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_MEM_LEAK:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '')?.length
              ? formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '')
              : formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_FD_LEAK:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_NATIVE_MEM:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_BITMAP:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          default:
            return formatKeyMethodsByKeyValue(item?.commonResult?.keyMethod ?? '');
        }
      };
      return ({
        ...item?.commonResult,
        ...diffData,
        tagInfo,
        isCompare: false,
        tagInfoList: tagInfo,
        keyMethodList: getKeyMethods(),
        token: token || params?.token || '',
        feature: item?.commonResult?.issueId,
        issueIndex: getIssueIndex(page, pageSize, index),
        issueFeature: {
          issueId: item.commonResult?.issueId,
          exceptionMessage: item.commonResult?.exceptionMsg,
          exceptionName: item.commonResult?.exceptionName,
          keyStack: item.commonResult?.keyMethod,
          tagList: item.commonResult?.tags,
        },
      });
    });

    return {
      list: data,
      data,
      total: res.total,
      topNum: 0,
      coverageRate: 0,
      totalDocCount: res.totalDocCount,
      totalQimeiCount: res.totalQimeiCount,
      topIssueCount: res.topIssueCount,
      topCaseRatio: res.topCaseRatio,
    };
  } catch (e) {
    logger.error('getIssueList:', e);
    return {
      list: [],
      data: [],
      total: 0,
      topNum: 0,
      coverageRate: 0,
      totalDocCount: 0,
      totalQimeiCount: 0,
    };
  }
};

export const getCompareIssueList = async (params: IGetIssueListReq, issueType: IssueType, token = '') => {
  try {
    const countPage = (params.start! / params.rows!) + 1 || initPageSetting.current;
    const page = params.page ?? countPage;
    const pageSize = params.pageSize ?? params.rows ?? initPageSetting.pageSize;
    const reqBody: IGetIssueListReq = {
      page,
      pageSize,
      issueType,
      productId: getProductId(),
      compareStatus: params?.compareStatus ?? 0,
      sortField: getSortField(params.sortField ?? ''),
      sortType: params.sortType ?? getSortType(params.sortOrder),
      sortABRatio: getSortABRatio(params.sortField ?? ''),
      token: token || params?.token || getParamFromUrl('token'),
      compare: true, // 模式：false 问题列表模式，true对比列表模式。暂时只支持对比列表模式
    };
    const res: IGetIssueListRsp = await request.post(`/${API_PREFIX}/trpc/trpc.rmonitor.web_server.IssueList/GetIssueList`, reqBody);
    const data = res?.issueList?.map((item: IIssueInfo, index: number) => {
      const diffResult = item?.diffResult?.map((diff: IDiffInfo) => {
        const name = toHumpString(diff?.name);
        return ({
          [`${name}ValueA`]: diff.valueA, // 对比模式下，对比条件A的字段值
          [`${name}ValueB`]: diff.valueB, // 对比模式下，对比条件B的字段值
          [`${name}StrValueA`]: diff.strValueA, // 对比模式下，对比条件A的字段值
          [`${name}StrValueB`]: diff.strValueB, // 对比模式下，对比条件B的字段值
          [`${name}Ratio`]: decimal2Percentile(Number(diff.ratio), 4, false), // 对比模式下，AB条件中该字段值的变化率
          // 以下字段仅针对崩溃/ANR/FOOM/错误的发生次数，影响设备数
          [`${name}Variation`]: diff.variation, // 对比模式下，变化量【发生次数（A）- 发生次数（B）】
          [`${name}VariationProportion`]: decimal2Percentile(Number(diff.variationProportion), 4, false), // 对比模式下，变化量占比【发生次数（变化量）/A总发生次数-B总发生次数】
        });
      });
      const diffData = diffResult?.reduce((prev: any, current: any) => ({ ...prev, ...current }), {});

      const tagInfo = item?.commonResult?.tags?.map(item => ({
        tagId: item.id,
        tagName: item.name,
        tagColor: item.color,
      }));

      const getKeyMethods = () => {
        switch (issueType) {
          case IssueType.ISSUE_TYPE_HIGH_MEMORY:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_FOOM:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_FOOM_MALLOC:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_BADMALLOC:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_MEM_LEAK:
            return formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '')?.length
              ? formatKeyMethodsByJson(item?.commonResult?.keyMethod ?? '')
              : formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_FD_LEAK:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_NATIVE_MEM:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          case IssueType.ISSUE_TYPE_BITMAP:
            return formatKeyMethodsByLine(item?.commonResult?.keyMethod ?? '');
          default:
            return formatKeyMethodsByKeyValue(item?.commonResult?.keyMethod ?? '');
        }
      };

      return ({
        ...item?.commonResult,
        ...diffData,
        tagInfo,
        isCompare: true,
        tagInfoList: tagInfo,
        keyMethodList: getKeyMethods(),
        token: token || params?.token || '',
        feature: item?.commonResult?.issueId,
        issueIndex: getIssueIndex(page, pageSize, index),
      });
    });

    return {
      list: data,
      data,
      total: res.total,
      topNum: 0,
      coverageRate: 0,
      totalDocCount: res.totalDocCount,
      totalQimeiCount: res.totalQimeiCount,
      topIssueCount: res.topIssueCount,
      topCaseRatio: res.topCaseRatio,
    };
  } catch (e) {
    logger.error('getCompareIssueList:', e);
    return {
      list: [],
      data: [],
      total: 0,
      topNum: 0,
      coverageRate: 0,
      totalDocCount: 0,
      totalQimeiCount: 0,
    };
  }
};

export const getSortField = (sortField: string) => {
  const valueA = '_value_a';
  const valueB = '_value_b';
  const strValueA = '_str_value_a';
  const strValueB = '_str_value_b';
  const ratio = '_ratio';
  const variation = '_variation';
  const variationProportion = '_variation_proportion';
  return toLineString(sortField)
    .replace(strValueA, '')
    .replace(strValueB, '')
    .replace(valueA, '')
    .replace(valueB, '')
    .replace(ratio, '')
    .replace(variationProportion, '')
    .replace(variation, '');
};

const getSortABRatio = (sortField: string) => {
  const valueA = '_value_a';
  const valueB = '_value_b';
  const ratio = '_ratio';
  const variation = '_variation';
  const variationProportion = '_variation_proportion';
  const lineField = toLineString(sortField);
  if (lineField.includes(valueA)) return 'A';
  if (lineField.includes(valueB)) return 'B';
  if (lineField.includes(ratio)) return 'RATIO';
  if (lineField.includes(variation) || lineField.includes(variationProportion)) return 'VARIATION';
  return 'A';
};

const getSortType = (sortType?: string) => {
  switch (sortType) {
    case 'asc':
      return 'ASC';
    case 'desc':
    default:
      return 'DESC';
  }
};
